import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FormRenderer from "../../../../../components/generic/formbuilder/renderer/FormRenderer";
import { Spin, notification } from "antd";
import CustomModal from "../../../../../components/generic/custommodal/CustomModal";
import PrimaryButton from "../../../../../components/generic/buttons/PrimaryButton";
import DataAnalyzer from "../../../dataanalyzerui/DataAnalyzer";
import { useGetOrgIdentifier } from "../../../hooks/useGetOrgIdentifier";
import apiService from "../../../../../service/apiservice/apiService";
import { strings } from "../../../../utils/strings";
import {
  convertDateToSODTimestamp,
  getDateString,
} from "../../../../../utils/dateutils";
import { useReadMemory } from "../../../hooks/useReadMemory";
import MemoryType from "../../../../../service/memory/enum/MemoryType";
import { performPostRequest } from "../../../../../service/network/NetworkService";
import IntentType from "../../../../../intentexecutor/utils/IntentType";
import { getApiUrl } from "../../../../../service/network/UrlMapper";
import ErrorNotificationComponent from "../../../../../components/generic/error/ErrorNotificationComponent";

/**
 * Renders the forms by taking the template code from url path
 */
const FormViewerComponent = () => {
  const navigate = useNavigate();
  const [isDefaultDate, setIsDefaultDate] = useState(null);
  const [additionalFormDetails, setAdditionalFormDetails] = useState({});
  const query = new URLSearchParams(useLocation().search);
  const user = useReadMemory(MemoryType.USER);
  const templateCode = query.get("templateCode");
  const patientIdentifier = query.get("patientIdentifier");
  const workforceIdentifier = query.get("workforceIdentifier");
  let formIdentifier = query.get("formIdentifier");
  const queryFormIdentifier = query.get("formIdentifier");
  const opportunityId = query.get("opportunityId");
  const carePlanId = query.get("carePlanId");
  const taskId = query.get("taskId");
  const readOnlyString = query.get("readOnly");
  const formSummaryString = query.get("formSummary");
  const withrequest = query.get("withrequest");
  const readOnly = readOnlyString === "true";
  const formSummary = formSummaryString === "true";
  const [formComments, setFormComments] = useState("");
  const [formApproveComments, setFormApproveComments] = useState("");
  const [summarisedFormData, setSummarisedData] = useState(undefined);
  const [openAnalyzerModal, setOpenAnayzerModal] = useState(false);
  const organizationIdentifier = useGetOrgIdentifier();
  const [opportunities, setOpportunities] = useState([]);
  const [isApproveCommentsAllowed, setIsApproveCommentsAllowed] = useState(null);

  const navigateToPreviousScreen = useCallback((identifier, status) => {
    let message;
    if (identifier && identifier !== "undefined") {
      message = strings.FORM_UPPDATED_SUCCESS;
    } else if (status === "Filled") {
      message = strings.FORM_SAVED_SUCCESS;
    } else if (status === "Submitted") {
      message = strings.FORM_SUBMITTED_SUCCESS;
    }

    notification.success({
      message,
      placement: "bottomRight",
    });
    setTimeout(() => navigate(-1), 100);
  }, [navigate]);

  const formSubmitCallback = async (formData, status, volunteerId) => {
    const taskFormIdentifier = additionalFormDetails?.formIdentifier
      ? JSON.parse(additionalFormDetails?.formIdentifier)
      : [];
    const hasOldTaskIdentifier = taskFormIdentifier?.some(
      (eachDeliveryFormIdentifier) =>
        eachDeliveryFormIdentifier?.formIdentifier === formData?.formIdentifier
    );
    if (!hasOldTaskIdentifier) {
      taskFormIdentifier.push({
        templateCode,
        formIdentifier: formData?.formIdentifier,
        formName: formData?.formName,
      });
    }
    const formIdentifier = JSON.stringify(taskFormIdentifier);
    const opportunity = opportunities?.find(item => item?.workforceIdentifier === volunteerId)

    const payload = {
      ...(taskId && taskId !== "undefined" && { id: taskId }),
      opportunityId: opportunityId && opportunityId !== "undefined" ? opportunityId : opportunity?.id,
      workforceIdentifier: workforceIdentifier && workforceIdentifier !== "undefined" ? workforceIdentifier : volunteerId,
      organizationIdentifier,
      taskDate: additionalFormDetails?.taskDate,
      startTime: additionalFormDetails?.startTime,
      endTime: additionalFormDetails?.endTime,
      status: status,
      formIdentifier: formIdentifier,
      ...(user?.jobTitle === "Volunteer Coordinator" && (!taskId || taskId === "undefined" || additionalFormDetails?.coordinatorIdentifier) && { coordinatorIdentifier: user?.userIdentifier }),
    };
    if (Boolean(withrequest)) {
      await apiService.post("niramayafoundation/niramayavisits/api/v1/task/withrequest", {
        ...payload,
        serviceType: "form",
      });
    } else {
      await apiService.post("niramayafoundation/niramayavisits/api/v1/task", { ...payload, serviceType: "task" });
    }
    navigateToPreviousScreen(queryFormIdentifier, status);
  };

  const dataAnalyzerCallback = (data, loading) => {
    setSummarisedData(data);
  };

  const dataAnalyserFooter = (
    <PrimaryButton
      buttonText="OK"
      handleOnClick={() => setOpenAnayzerModal(false)}
    />
  );

  useEffect(() => {
    const getCarePlanDetails = async () => {
      const endpoint = "niramayafoundation/niramayavisits/api/v1/careplans";
      const params = {
        organizationIdentifier,
        carePlanId,
      };
      const response = await apiService.get(endpoint, params);
      const result = response[0];

      const assignedOpportunities = result?.opportunities?.filter(
        (item) => item?.opportunityStatus === "Assigned"
      );
      setOpportunities(assignedOpportunities)        
    }

    if (carePlanId && carePlanId !== "undefined") {
      getCarePlanDetails();
    }
  }, [carePlanId, organizationIdentifier]);

  useEffect(() => {
    const getTaskDetails = async () => {
      const endpoint = "niramayafoundation/niramayavisits/api/v1/task";
      const params = {
        organizationIdentifier,
        id: taskId,
      };
      const response = await apiService.get(endpoint, params);
      if (response?.status === "Rejected") {
        const comments = response?.comments
          ? JSON.parse(response.comments)
          : null;
        const lastComment = comments?.length
          ? comments[comments.length - 1]?.comment
          : null;
        setFormComments(lastComment || "");
      }
      if (response?.status === "Approved") {
        setFormApproveComments(response?.approveComments);
      }
      setAdditionalFormDetails(response);
    };
    if (taskId && taskId !== "undefined") {
      getTaskDetails();
    }
  }, [organizationIdentifier, taskId]);

  useEffect(() => {
    const getConfig = async () => {
      const configPayload = {
        intent: IntentType.GET_CONFIGURATION,
        payload: {
          configurationKey: ["niramaya.visit.report.defaultDate", "niramaya.featureaccess.task.approve.comments"],
        },
        context: { organizationIdentifier },
      };

      try {
        const result = await performPostRequest(
          getApiUrl()[IntentType.GET_CONFIGURATION],
          JSON.stringify(configPayload),
        );
        const response = await result.json();

        const isDefault = Boolean(
          JSON.parse(response[0]?.configurationValue || "{}")?.isDefault,
        );

        const isApproveComments = Boolean(
          JSON.parse(response[1]?.configurationValue || "{}")?.visibility,
        );
        setIsApproveCommentsAllowed(isApproveComments)
        setIsDefaultDate(isDefault);
      } catch (error) {
        ErrorNotificationComponent(error);
      }
    };

    if (organizationIdentifier) {
      getConfig();
    }
  }, [organizationIdentifier]);

  useEffect(() => {
    if (isDefaultDate !== true || (taskId && taskId !== "undefined")) {
      return;
    }

    setAdditionalFormDetails((prev) =>
      prev?.taskDate
        ? prev
        : {
            ...prev,
            taskDate: convertDateToSODTimestamp(getDateString()),
          },
    );
  }, [isDefaultDate, taskId]);

  return (
    <>
      <FormRenderer
        templateCode={templateCode}
        formIdentifier={formIdentifier}
        workforceIdentifier={workforceIdentifier}
        patientIdentifier={patientIdentifier}
        formSubmitCallback={formSubmitCallback}
        isFormRenderedOnModal={false}
        formComments={formComments}
        formApproveComments={formApproveComments}
        dataAnalyzerCallback={dataAnalyzerCallback}
        readOnly={readOnly}
        formSummary={formSummary}
        additionalFormDetails={additionalFormDetails}
        setAdditionalFormDetails={setAdditionalFormDetails}
        setOpenAnayzerModal={setOpenAnayzerModal}
        opportunities={opportunities}
        taskId={taskId}
        isApproveCommentsAllowed={isApproveCommentsAllowed}
      />
      {openAnalyzerModal && (
        <CustomModal
          isModalOpen={openAnalyzerModal}
          closeModal={setOpenAnayzerModal}
          footer={summarisedFormData !== undefined && [dataAnalyserFooter]}
          title="Report Summary"
        >
          {summarisedFormData !== undefined ? (
            <DataAnalyzer data={summarisedFormData} />
          ) : (
            <div className="m-3">
              <Spin
                size="medium"
                className="d-flex justify-content-center m-auto"
              />
            </div>
          )}
        </CustomModal>
      )}
    </>
  );
};

export default FormViewerComponent;